import "./index.scss";

import { Box, FormControlLabel, Modal, Radio, Stack } from "@mui/material";
import { ReactComponent as DeleteIcon } from "assets/images/square-buttons/close-small.svg";
import Button from "components/Button";
import ScreenLoader from "components/ScreenLoader";
import TitleWithOptions from "components/TitleWithOptions";
import { CardioProgram, StrengthProgram, WorkoutPlan } from "models/workoutplan";
import { getLkpGoalsList } from "modules/lookup/lookupServices";
import {
  getLkpFlexibilityStretchesLowerBodyList,
  getLkpFlexibilityStretchesUpperBodyList,
  getLkpPerceivedExertionList,
  getWorkoutPlanById,
} from "modules/workout-plan/workoutPlanServices";
import { useEffect, useRef, useState } from "react";

import GeneratePdf, { GeneratePdfRef } from "./GeneratePdf";

const WorkoutCardModal = (props: {
  open: boolean;
  onClose: any;
  memberId?: number;
  memberName: string;
  workoutPlan?: WorkoutPlan;
  perceivedExertionOptionsFromParent?: any[];
  workoutPlanGoalsOptionsFromParent?: any[];
  flexibilityUpperBodyStretchesOptionsFromParent?: any[];
  flexibilityLowerBodyStretchesOptionsFromParent?: any[];
}) => {
  const {
    open,
    onClose,
    memberId,
    memberName,
    workoutPlan,
    perceivedExertionOptionsFromParent,
    workoutPlanGoalsOptionsFromParent,
    flexibilityUpperBodyStretchesOptionsFromParent,
    flexibilityLowerBodyStretchesOptionsFromParent,
  } = props;
  const [currentWorkoutPlan, setCurrentWorkoutPlan] = useState<WorkoutPlan>();
  const [selectedCardioProgram, setSelectedCardioProgram] = useState<CardioProgram>();
  const [selectedStrengthProgram, setSelectedStrengthProgram] = useState<StrengthProgram>();
  const [loading, setLoading] = useState(false);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);
  const [perceivedExertionOptions, setPerceivedExertionOptions] = useState([]);
  const [workoutPlanGoalsOptions, setWorkoutPlanGoalsOptions] = useState([]);
  const [flexibilityUpperBodyStretchesOptions, setFlexibilityUpperBodyStretchesOptions] = useState(
    []
  );
  const [flexibilityLowerBodyStretchesOptions, setFlexibilityLowerBodyStretchesOptions] = useState(
    []
  );
  const pdfRef = useRef<GeneratePdfRef>(null);

  useEffect(() => {
    if (open) {
      if (memberId) {
        //Workout plan and its lookups need to be fetched (this is for print outs from members listing page)
        setLoading(true);
        let promises: Promise<any>[] = [];

        //Get workout plan
        promises.push(
          getWorkoutPlanById({ member_id: memberId })
            .then((res) => {
              setCurrentWorkoutPlan(res.data);
            })
            .catch((e) => console.log(e))
        );

        if (!lookupsLoaded) {
          //Get plan info goals' list
          promises.push(
            getLkpGoalsList().then((res) => {
              if (res.success) {
                let goals: any[] = [];
                Object.keys(res.data).forEach((e) => {
                  goals.push({ label: res.data[e], value: e });
                });
                setWorkoutPlanGoalsOptions(goals);
              }
            })
          );

          // //Get flexibility upper body stretches' list
          promises.push(
            getLkpFlexibilityStretchesUpperBodyList().then((res) => {
              if (res.success) {
                setFlexibilityUpperBodyStretchesOptions(res.data);
              }
            })
          );

          // //Get flexibility lower body stretches' list
          promises.push(
            getLkpFlexibilityStretchesLowerBodyList().then((res) => {
              if (res.success) {
                setFlexibilityLowerBodyStretchesOptions(res.data);
              }
            })
          );

          //Get list for perceivedExertionOptions
          promises.push(
            getLkpPerceivedExertionList().then((res) => {
              if (res.success) {
                let entries: any[] = [];
                Object.keys(res.data).forEach((e) => {
                  entries.push({ label: `${e} - ${res.data[e]}`, value: e });
                });
                setPerceivedExertionOptions(entries);
              }
            })
          );
        }

        Promise.all(promises).then((_v) => {
          setLoading(false);
          setLookupsLoaded(true);
        });
      }
    }
    if (workoutPlan) {
      setCurrentWorkoutPlan(workoutPlan);
      setPerceivedExertionOptions(perceivedExertionOptionsFromParent || []);
      setWorkoutPlanGoalsOptions(workoutPlanGoalsOptionsFromParent || []);
      setFlexibilityUpperBodyStretchesOptions(flexibilityUpperBodyStretchesOptionsFromParent || []);
      setFlexibilityLowerBodyStretchesOptions(flexibilityLowerBodyStretchesOptionsFromParent || []);
    }
  }, [memberId, open, workoutPlan]);

  useEffect(() => {
    if (currentWorkoutPlan) {
      if (currentWorkoutPlan.cardio_workouts?.length) {
        setSelectedCardioProgram(currentWorkoutPlan.cardio_workouts[0]);
      }
      if (currentWorkoutPlan.strength_workouts?.length) {
        setSelectedStrengthProgram(currentWorkoutPlan.strength_workouts[0]);
      }
    }
  }, [currentWorkoutPlan]);

  //#region event handlers
  const handleCloseModal = () => {
    onClose();
  };

  const handleChangeCardioProgram = (id: number) => {
    setSelectedCardioProgram(currentWorkoutPlan.cardio_workouts.find((f) => f.id === id));
  };

  const handleChangeStrengthProgram = (id: number) => {
    setSelectedStrengthProgram(currentWorkoutPlan.strength_workouts.find((f) => f.id === id));
  };
  //#endregion event handlers

  if (!memberId && !workoutPlan) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack className="personal-details-modal-container">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          marginBottom="40px"
          className="header"
        >
          <h1>Setup Workout Card Printing</h1>
          <DeleteIcon width="24px" height="24px" onClick={handleCloseModal} />
        </Stack>
        {loading && <ScreenLoader isScreenLoader />}
        {!loading && (
          <Stack height={"100%"}>
            <Box mt={4} flexGrow={1}>
              {currentWorkoutPlan?.isCardioProgramIncluded && (
                <>
                  <TitleWithOptions title="Cardio Program" />
                  <Stack direction="row" className="workoutcard-program-container" mb={4}>
                    <Stack
                      flex={1}
                      borderRight={1}
                      borderColor="#D6EEEC"
                      className="left-container"
                      gap={1}
                    >
                      <Stack direction="row" className="title-row">
                        <p className="name">Programs</p>
                      </Stack>
                      {currentWorkoutPlan?.cardio_workouts?.map((m, i) => (
                        <Stack
                          direction="row"
                          className={`item ${m.id === selectedCardioProgram?.id ? "active" : ""}`}
                          justifyContent="space-between"
                          alignItems="center"
                          key={`workoutCardCardioProgram${i}`}
                        >
                          <FormControlLabel
                            value={m.id}
                            control={
                              <Radio
                                checked={m.id === selectedCardioProgram?.id}
                                sx={{
                                  "&.Mui-checked": {
                                    color: m.id === selectedCardioProgram?.id ? "white" : "#33A9A0",
                                  },
                                  color: "#D6EEEC",
                                }}
                                onChange={(e) => handleChangeCardioProgram(+e.target.value)}
                              />
                            }
                            label={m.name}
                          />
                          <Box className="triangle"></Box>
                        </Stack>
                      ))}
                    </Stack>
                    <Stack flex={2} className="right-container" gap={1}>
                      <Stack direction="row" className="title-row">
                        <p className="name">Exercise</p>
                        <p className="val1">Time (in minutes)</p>
                        <p className="val2"></p>
                      </Stack>
                      {selectedCardioProgram?.exercises?.map((m, i) => (
                        <Stack
                          direction="row"
                          className="data-row"
                          key={`cardioProgramExercise${i}`}
                        >
                          <p className="name">{m.name}</p>
                          <p className="val1">{m.time}</p>
                          <p className="val2"></p>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </>
              )}

              {currentWorkoutPlan?.isStrengthProgramIncluded && (
                <>
                  <TitleWithOptions title="Strength Program" />
                  <Stack direction="row" className="workoutcard-program-container">
                    <Stack
                      flex={1}
                      borderRight={1}
                      borderColor="#D6EEEC"
                      className="left-container"
                    >
                      <Stack direction="row" className="title-row">
                        <p className="name">Programs</p>
                      </Stack>
                      {currentWorkoutPlan?.strength_workouts?.map((m, i) => (
                        <Stack
                          direction="row"
                          className={`item ${m.id === selectedStrengthProgram?.id ? "active" : ""}`}
                          justifyContent="space-between"
                          alignItems="center"
                          key={`workoutCardStrengthProgram${i}`}
                        >
                          <FormControlLabel
                            value={m.id}
                            control={
                              <Radio
                                checked={m.id === selectedStrengthProgram?.id}
                                sx={{
                                  "&.Mui-checked": {
                                    color:
                                      m.id === selectedStrengthProgram?.id ? "white" : "#33A9A0",
                                  },
                                  color: "#D6EEEC",
                                }}
                                onChange={(e) => handleChangeStrengthProgram(+e.target.value)}
                              />
                            }
                            label={m.name}
                          />
                          <Box className="triangle"></Box>
                        </Stack>
                      ))}
                    </Stack>
                    <Stack flex={2} className="right-container" gap={1}>
                      <Stack direction="row" className="title-row">
                        <p className="name">Exercise</p>
                        <p className="val1">Min Lift Time</p>
                        <p className="val2">Min Drop Time</p>
                      </Stack>
                      {selectedStrengthProgram?.exercises?.map((m, i) => (
                        <Stack
                          direction="row"
                          className="data-row"
                          key={`cardioProgramExercise${i}`}
                        >
                          <p className="name">{m.name}</p>
                          <p className="val1">{m.time_up_for_display}</p>
                          <p className="val2">{m.time_down_for_display}</p>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </>
              )}
            </Box>
            <Stack
              className="actions-container"
              direction="row"
              gap={1}
              mt={2}
              justifyContent="flex-end"
            >
              <Button
                label={"Cancel"}
                className="margin-0 transparent-bg color-primary"
                onClick={handleCloseModal}
              />
              <Button
                label={"Print"}
                className="margin-0"
                onClick={() => pdfRef?.current.printContent()}
              />
            </Stack>
            <Box className="visually-hidden">
              <GeneratePdf
                ref={pdfRef}
                title={memberName}
                type="workout-card"
                workoutPlan={currentWorkoutPlan}
                cardioProgram={selectedCardioProgram}
                strengthProgram={selectedStrengthProgram}
                perceivedExertion={
                  perceivedExertionOptions?.find(
                    (f) => +f.value === currentWorkoutPlan?.perceived_exertion
                  )?.label || ``
                }
                workoutPlanGoals={workoutPlanGoalsOptions
                  .filter((f) => currentWorkoutPlan?.goals?.includes(f.value))
                  .map((m) => m.label)
                  .join(", ")}
                flexibilityUpperBodyStretchesOptionsFromParent={
                  flexibilityUpperBodyStretchesOptions
                }
                flexibilityLowerBodyStretchesOptionsFromParent={
                  flexibilityLowerBodyStretchesOptions
                }
              />
            </Box>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};

export default WorkoutCardModal;
